/**
 * Reads a chunked newline-delimited-JSON response of the shape used by
 * /api/studio-team/generate: `{"type":"heartbeat"}` lines to keep the
 * connection alive through long-running generations, a final
 * `{"type":"complete", ...payload}` line, or `{"type":"error","message"}`
 * on a failure that happens after the server already committed to a 200
 * response (so it can't fall back to an HTTP error status).
 *
 * Shared by useStudioTeam.ts and RouletteTab.tsx's direct call, so there's
 * one place to fix if the wire format ever changes -- not two.
 */
export async function readNdjsonStream<T>(res: Response): Promise<T> {
  if (!res.body) throw new Error('Response had no body');
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: T | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.trim()) continue;
      const event = JSON.parse(line) as { type: string; message?: string } & Record<string, unknown>;
      if (event.type === 'error') {
        throw new Error(event.message || 'Generation failed');
      } else if (event.type === 'complete') {
        const { type: _type, ...rest } = event;
        result = rest as unknown as T;
      }
      // 'heartbeat' events are just keep-alive pings — nothing to do
    }
  }

  if (!result) throw new Error('Stream ended without a result');
  return result;
}
